import { EllipsisVertical } from 'lucide-react';

import { Button } from '@nesso/ui/primitives/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@nesso/ui/primitives/dropdown-menu';
import { Item, ItemActions, ItemContent, ItemGroup } from '@nesso/ui/primitives/item';

import {
    type ClientContact,
    CONTACT_RELATIONSHIP_LABELS,
    type GetClientContactsOutput,
    openClientChartInPlace,
} from '@/entities/client';
import { ReachContactValue } from '@/features/client/reach-contact';
import { useSheetStore } from '@/shared/stores/sheet-store';
import { ContactNotes } from '@/shared/ui/common/contact-notes';
import { ContactRoleTags } from '@/shared/ui/common/contact-role-tags';
import { DotSeparatedText } from '@/shared/ui/text/dot-separated-text';

type Props = {
    contacts: GetClientContactsOutput | undefined;
    isError: boolean;
};

// A client with no contacts renders nothing beneath the header: the section's own Add Contact
// button is the empty state (the Contacts frame shows no other copy). The chart holds its spinner
// until the first read settles, so there is no in-flight state to render here.
export function ClientDetailsContacts({ contacts, isError }: Props) {
    const openSheet = useSheetStore((state) => state.openSheet);

    // A person who is also a client has a chart of their own, which is the fuller profile.
    const openContact = (contact: ClientContact) => {
        if (contact.client) {
            openClientChartInPlace(contact.client);
            return;
        }

        const name = `${contact.firstName} ${contact.lastName}`;
        openSheet({
            type: 'clients/contact-details',
            label: name,
            data: { personId: contact.personId, name },
        });
    };

    // An unread list must not pass for an empty one: a therapist looking for the emergency contact
    // would take silence for "none recorded".
    if (isError && !contacts) {
        return (
            <p className="pt-2 text-sm text-muted-foreground">
                Unable to load contacts. Try again later.
            </p>
        );
    }

    if (!contacts?.items.length) return null;

    return (
        <ItemGroup className="gap-3 pt-3 pb-2">
            {contacts.items.map((contact) => {
                const name = `${contact.firstName} ${contact.lastName}`;
                const email = contact.email?.trim();
                const phone = contact.phone?.trim();

                return (
                    <div key={contact.personId} role="listitem">
                        <Item variant="outline">
                            <ItemContent>
                                <div className="text-base">
                                    <span className="font-semibold">{name}</span>
                                    <span className="text-muted-foreground">
                                        ,{' '}
                                        {CONTACT_RELATIONSHIP_LABELS[
                                            contact.relationship
                                        ].toLowerCase()}
                                    </span>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <ContactRoleTags
                                        isEmergencyContact={contact.isEmergencyContact}
                                        isBillingContact={contact.isBillingContact}
                                    />
                                    <DotSeparatedText
                                        items={[
                                            email && (
                                                <ReachContactValue
                                                    key="email"
                                                    kind="email"
                                                    value={email}
                                                    size="sm"
                                                />
                                            ),
                                            phone && (
                                                <ReachContactValue
                                                    key="phone"
                                                    kind="phone"
                                                    value={phone}
                                                    size="sm"
                                                />
                                            ),
                                        ]}
                                        orientation="responsive"
                                        size="sm"
                                    />
                                    <ContactNotes notes={contact.notes} />
                                </div>
                            </ItemContent>
                            <ItemActions>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon-sm"
                                            aria-label={`Contact options for ${name}`}
                                        >
                                            <EllipsisVertical className="size-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-auto">
                                        <DropdownMenuItem onSelect={() => openContact(contact)}>
                                            Contact profile
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </ItemActions>
                        </Item>
                    </div>
                );
            })}
        </ItemGroup>
    );
}
